import React, { useEffect, useState } from 'react';
import { MoreInfoOutlined, WarningAltOutlined } from '@duxton-web/icons';
import {
  Card,
  Collapse,
  ConfigProvider,
  Divider,
  Form,
  FormListFieldData,
  Select,
  Switch,
  Tag,
  Tooltip,
  Typography,
} from '@duxton-web/ui';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import './index.css';

import { produce } from 'immer';

import Overlay from '../../components/overlay';
import {
  ExperimentAssignmentStrategy,
  getGeography,
  getTreatmentName,
  getVariables,
  isEditMode,
  isViewMode,
  RegionalCountries,
  TreatmentDefaultColors,
} from '../../constants';
import {
  useExperimentContext,
  useExperimentDispatchContext,
} from '../../contexts/experiment-context';
import {
  updateAssignmentStrategy,
  updateConfiguration,
} from '../../store/actions/experiment/action-creators';
import AddConfiguration from './add-configuration';
import AddTreatment from './add-treatment';
import AddVariable from './add-variable';
import AssignmentStrategy from './assignment-strategy';
import CloneConfiguration from './clone-configuration';
import DeleteConfiguration from './delete-configuration';
import DeleteTreatment from './delete-treatment';
import DeleteVariable from './delete-variable';
import EditTreatment from './edit-treatment';
import TreatmentControl from './treatment-control';
import { getCountryNameByCountryCode } from '@/common/CountryCodesMap';
import { ABORTED, REQUESTED } from '@/constants';
import { useGetRegionalExperimentChildren } from '@/hooks';

const ExperimentConfigurationHeader = ({
  index,
  ...configuration
}: FormListFieldData & { index: number }) => {
  const params = useParams<{ action: string }>();
  const action = params.action;
  const readOnly = isViewMode(action);

  const experimentContext = useExperimentContext();
  const { configurations } = experimentContext;
  const variables = getVariables(configurations);
  const choices = variables?.[0]?.choices;

  const form = Form.useFormInstance();

  if (!choices?.length)
    return (
      <thead>
        <tr>
          <th
            style={{
              padding: '4px 16px',
              background: '#FFF',
              borderRight: '1px solid #E8E8E8',
            }}
          >
            <div style={{ width: 320, textAlign: 'left' }}>Variable</div>
          </th>
          <th
            style={{
              padding: '4px 16px',
              background: '#FFF',
              borderRight: '1px solid #E8E8E8',
              zIndex: 0,
              minWidth: 400,
              maxWidth: 400,
            }}
          >
            <TreatmentTag
              key={`experiment-0-treatment-0-header`}
              color={`${TreatmentDefaultColors[0]}1A`}
              style={{
                padding: '6px 16px',
                color: '#3D3D3D',
                margin: 0,
                width: '100%',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <TreatmentDot color={TreatmentDefaultColors[0]} />
                  Control
                  <MoreInfoOutlined />
                </div>
              </div>
            </TreatmentTag>
          </th>
          <th
            style={{
              padding: '4px 16px',
              background: '#FFF',
              borderRight: '1px solid #E8E8E8',
              zIndex: 0,
              minWidth: 400,
              maxWidth: 400,
            }}
          >
            <TreatmentTag
              key={`experiment-1-treatment-1-header`}
              color={`${TreatmentDefaultColors[1]}1A`}
              style={{
                padding: '6px 16px',
                color: '#3D3D3D',
                margin: 0,
                width: '100%',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <TreatmentDot color={TreatmentDefaultColors[1]} />
                  Treatment A
                  <MoreInfoOutlined />
                </div>
              </div>
            </TreatmentTag>
          </th>
        </tr>
      </thead>
    );

  return (
    <thead>
      <tr>
        <th
          style={{
            padding: '4px 16px',
            background: '#FFF',
            borderRight: '1px solid #E8E8E8',
          }}
        >
          <div style={{ width: 320, textAlign: 'left' }}>Variable</div>
        </th>
        <Form.List name={[configuration.name, 'variables']}>
          {(variables) =>
            variables.map((variable) => {
              if (variable.key > 0) return null;

              return (
                <Form.List key={variable.key} name={[variable.name, 'choices']}>
                  {(choices) =>
                    choices.map((choice) => {
                      return (
                        <th
                          key={choice.key}
                          style={{
                            padding: '4px 16px',
                            background: '#FFF',
                            borderRight: '1px solid #E8E8E8',
                            zIndex: 0,
                            minWidth: 400,
                            maxWidth: 400,
                          }}
                        >
                          <TreatmentTag
                            color={`${TreatmentDefaultColors[choice.key]}1A`}
                            style={{
                              padding: '6px 16px',
                              color: '#3D3D3D',
                              margin: 0,
                              width: '100%',
                              minHeight: 40,
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                width: '100%',
                              }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8,
                                }}
                              >
                                <TreatmentDot
                                  color={TreatmentDefaultColors[choice.key]}
                                />
                                {getTreatmentName(
                                  form.getFieldValue([
                                    'configurations',
                                    configuration.name,
                                    'variables',
                                    variable.name,
                                    'choices',
                                    choice.name,
                                  ]),
                                  choice.key,
                                )}
                                <MoreInfoOutlined />
                              </div>
                              {!readOnly && (
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}
                                >
                                  {index === 0 && (
                                    <EditTreatment
                                      index={choice.key}
                                      name={
                                        form.getFieldValue([
                                          'configurations',
                                          configuration.name,
                                          'variables',
                                          variable.name,
                                          'choices',
                                          choice.name,
                                        ])?.name
                                      }
                                    />
                                  )}
                                  {choice.key > 1 && index === 0 && (
                                    <Divider
                                      type="vertical"
                                      style={{ marginInline: 0 }}
                                    />
                                  )}
                                  {choice.key > 1 && index === 0 && (
                                    <DeleteTreatment index={choice.key} />
                                  )}
                                </div>
                              )}
                            </div>
                          </TreatmentTag>
                        </th>
                      );
                    })
                  }
                </Form.List>
              );
            })
          }
        </Form.List>
      </tr>
    </thead>
  );
};

const ExperimentConfigurationBody = ({
  index,
  ...configuration
}: FormListFieldData & { index: number }) => {
  const experimentContext = useExperimentContext();
  const { configurations } = experimentContext;
  const variables = getVariables(configurations);
  const choices = variables?.[0]?.choices;

  const form = Form.useFormInstance();

  if (!choices?.length)
    return (
      <tbody>
        <tr>
          <td
            style={{
              padding: '4px 16px',
              background: '#FFF',
              borderRight: '1px solid #E8E8E8',
            }}
          >
            <div style={{ width: 320, textAlign: 'left' }} />
          </td>
          <td
            style={{
              padding: '4px 16px',
              background: '#FFF',
              borderRight: '1px solid #E8E8E8',
              zIndex: 0,
              minWidth: 400,
              maxWidth: 400,
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                width: '100%',
                padding: '0 16px',
              }}
            >
              <Typography.Text strong>
                Add Variable to Start Experimenting
              </Typography.Text>
              <Typography.Text>
                Start by adding a variable which will reflect on all treatments
                where you can change it’s value for each treatment.
              </Typography.Text>
            </div>
          </td>
          <td
            style={{
              padding: '4px 16px',
              background: '#FFF',
              borderRight: '1px solid #E8E8E8',
              zIndex: 0,
              minWidth: 400,
              maxWidth: 400,
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                width: '100%',
                padding: '0 16px',
              }}
            >
              <Typography.Text strong>
                Add Variable to Start Experimenting
              </Typography.Text>
              <Typography.Text>
                Start by adding a variable which will reflect on all treatments
                where you can change it’s value for each treatment.
              </Typography.Text>
            </div>
          </td>
        </tr>
      </tbody>
    );

  return (
    <tbody>
      <Form.List name={[configuration.name, 'variables']}>
        {(variables) =>
          variables.map((variable) => {
            return (
              <tr key={variable.key}>
                <td
                  style={{
                    padding: '4px 0',
                    paddingRight: 16,
                    background: '#FFF',
                    borderRight: '1px solid #E8E8E8',
                    verticalAlign: 'top',
                  }}
                >
                  <DeleteVariable
                    variableName={form.getFieldValue([
                      'configurations',
                      index,
                      'variables',
                      variable.name,
                      'name',
                    ])}
                  />
                </td>
                <Form.List key={variable.key} name={[variable.name, 'choices']}>
                  {(choices) =>
                    choices.map((choice) => {
                      const variableName = form.getFieldValue([
                        'configurations',
                        index,
                        'variables',
                        variable.name,
                        'name',
                      ]);

                      return (
                        <td
                          key={choice.key}
                          style={{
                            padding: '4px 16px',
                            background: '#FFF',
                            borderRight: '1px solid #E8E8E8',
                            zIndex: 0,
                            minWidth: 400,
                            maxWidth: 400,
                            verticalAlign: 'top',
                          }}
                        >
                          <TreatmentControl
                            configurationIndex={configuration.name}
                            variableName={variableName}
                            variableIndex={variable.name}
                            choiceIndex={choice.name}
                          />
                        </td>
                      );
                    })
                  }
                </Form.List>
              </tr>
            );
          })
        }
      </Form.List>
    </tbody>
  );
};

const ExperimentConfiguration = ({
  personalizeByCountry,
  index,
  ...configuration
}: FormListFieldData & {
  personalizeByCountry: boolean;
  index: number;
}) => {
  const params = useParams<{ action: string }>();
  const action = params.action;
  const disabled = isViewMode(action);

  const experimentContext = useExperimentContext();
  const { experiment, configurations } = experimentContext;
  const restrictByValues =
    configurations?.flatMap((c, i: number) => {
      if (index !== i) return c.country_code || [];
    }) || [];

  const form = Form.useFormInstance();
  const countryCode = form.getFieldValue([
    'configurations',
    index,
    'country_code',
  ]);

  const { data } = useGetRegionalExperimentChildren(
    experiment?.name,
    countryCode,
    {
      enabled: personalizeByCountry,
    },
  );

  return (
    <Card key={configuration.key} styles={{ body: { padding: '12px 16px' } }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {personalizeByCountry && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Form.Item
                name={[configuration.name, 'country_code']}
                style={{ marginBottom: 0 }}
              >
                <Select
                  disabled={disabled || isEditMode(action)}
                  popupMatchSelectWidth
                  style={{ minWidth: 320, width: 'fit-content' }}
                  placeholder="Select country"
                  options={RegionalCountries.map((c) => {
                    return {
                      disabled: restrictByValues.includes(c),
                      value: c,
                      label: getCountryNameByCountryCode(c),
                    };
                  })}
                  allowClear
                />
              </Form.Item>
              {disabled && data?.[0]?.state === ABORTED && (
                <Tag color="error" size="xsmall">
                  ABORTED
                </Tag>
              )}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <CloneConfiguration index={index} />
            <DeleteConfiguration index={index} />
          </div>
        </div>
        <div style={{ width: '100%', overflowX: 'auto' }}>
          <table className="experiment-treatment-table">
            <ExperimentConfigurationHeader index={index} {...configuration} />
            <ExperimentConfigurationBody index={index} {...configuration} />
          </table>
        </div>
      </div>
    </Card>
  );
};

const Treatment = () => {
  const params = useParams<{ action: string }>();
  const action = params.action;
  const readOnly = isViewMode(action);

  const experimentContext = useExperimentContext();
  const experimentDispatch = useExperimentDispatchContext();
  const { experiment, configurations } = experimentContext;
  const experimentDomain = experiment?.domain;
  const geography = getGeography(experimentDomain);

  const [personalizeByCountry, setPersonalizeByCountry] =
    useState<boolean>(false);

  const showOverlay = isEditMode(action) && experiment?.state !== REQUESTED;

  useEffect(() => {
    setPersonalizeByCountry(!!experiment?.regionalExperimentName);
  }, [experiment?.regionalExperimentName]);

  useEffect(() => {
    if (geography !== 'multi') {
      setPersonalizeByCountry(false);
    }
  }, [geography]);

  return (
    <ConfigProvider
      theme={{
        components: {
          Table: {
            headerBg: 'white',
          },
        },
      }}
    >
      <Card
        styles={{
          body: {
            padding: 0,
          },
        }}
      >
        <Overlay
          subTitle="Editing of treatments is not allowed in current state."
          showOverlay={showOverlay}
        />
        <Collapse
          expandIconPosition="end"
          defaultActiveKey={['treatments']}
          style={{ backgroundColor: 'white' }}
          bordered={false}
          collapsible="icon"
          items={[
            {
              key: 'treatments',
              label: (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    height: 32,
                  }}
                >
                  <Typography.Text strong>Treatments</Typography.Text>
                  {!readOnly && geography === 'multi' && (
                    <Divider
                      type="vertical"
                      style={{ marginInline: 0, margin: 0 }}
                    />
                  )}
                  {!readOnly && geography === 'multi' && (
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                    >
                      <Switch
                        onChange={(val: boolean) => {
                          setPersonalizeByCountry(val);

                          if (!val) {
                            const nextConfigurations = produce(
                              configurations,
                              (draft) => {
                                draft[0].country_code = '';
                              },
                            );

                            experimentDispatch(
                              updateConfiguration([nextConfigurations[0]]),
                            );
                          }

                          if (val) {
                            experimentDispatch(
                              updateAssignmentStrategy(
                                ExperimentAssignmentStrategy.UNIFORM_CHOICE,
                              ),
                            );
                          }
                        }}
                      />
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                        }}
                      >
                        <Typography.Text>
                          Personalize by Country
                        </Typography.Text>
                        <Tooltip title="Enable this option only if you want to configure different treatments value for each">
                          <WarningAltOutlined />
                        </Tooltip>
                      </div>
                    </div>
                  )}
                </div>
              ),
              extra: (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <AddVariable />
                  <AddTreatment />
                </div>
              ),
              children: (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                  }}
                >
                  <Form.List name="configurations">
                    {(configurations) =>
                      configurations.map((configuration) => {
                        return (
                          <ExperimentConfiguration
                            key={configuration.key}
                            personalizeByCountry={personalizeByCountry}
                            {...configuration}
                            index={configuration.key}
                          />
                        );
                      })
                    }
                  </Form.List>
                  {personalizeByCountry && <AddConfiguration />}
                  <AssignmentStrategy
                    personalizeByCountry={personalizeByCountry}
                  />
                </div>
              ),
            },
          ]}
        />
      </Card>
    </ConfigProvider>
  );
};

export const TreatmentControlContainer = styled.div`
  box-shadow: -8px 0 8px -8px rgb(0 0 0 / 25%);
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  margin-bottom: 16px;
  padding-bottom: 32px;
  padding-left: 16px;
  z-index: 2;
`;

export const TreatmentDot = styled.div<{ color: string }>`
  border-radius: 16px;
  height: 16px;
  width: 16px;
  line-height: 0px;
  background-color: ${({ color }: { color: string }) => color};
`;

const TreatmentTag = styled.div<{ color: string }>`
  display: flex;
  align-items: center;
  height: 40px;
  border-radius: 8px;
  padding: 12px 16px;
  background-color: ${({ color }: { color: string }) => color};
`;

export default Treatment;
